import { TwoKeysSet } from "helpers/twoKeysDataStructures";
import { ICampaignServer } from "model/campaigns";
import { ILineitem } from "model/lineitems/lineitem";
import { AVAILABLE_IDS, IDefaultCampaignIdRange } from "./defaultCampaignId";
import {
  DEFAULT_DEFAULT_NETWORK_ID,
  DEFAULT_NETWORKS_SORTED_LIST,
  DefaultNetwork,
} from "./options";
import { ISkAdNetworkConfiguration } from "./skadNetwork";

export const noAvailableIdError = (appName: string) =>
  `Maximum number of line items reached for Campaign app ${appName}`;
export const SKAN4_ARCHIVE_WARNING =
  "This campaign is used for SKAdNetwork exploration. SKAN line item IDs may be unrecoverable after archive. Continue?";

function getAvailableIds(
  usedIds: TwoKeysSet,
  neededCount = 1
): {
  availableId: IDefaultCampaignIdRange;
  defaultNetworkId?: DefaultNetwork;
}[] {
  const availableIds: {
    availableId: IDefaultCampaignIdRange;
    defaultNetworkId: DefaultNetwork;
  }[] = [];
  for (const network of DEFAULT_NETWORKS_SORTED_LIST) {
    for (const id of AVAILABLE_IDS) {
      if (!usedIds.has(`${id}`, network)) {
        availableIds.push({
          availableId: `${id}` as IDefaultCampaignIdRange,
          defaultNetworkId: network,
        });
      }
    }
  }
  return availableIds.slice(0, neededCount);
}
const getAvailableId = (usedIds) => getAvailableIds(usedIds, 1)[0];
function getAvailableIdAcrossSameApp(sameAppCampaigns: ICampaignServer[] = []):
  | {
      availableId: IDefaultCampaignIdRange | undefined;
      defaultNetworkId?: DefaultNetwork;
    }
  | undefined {
  const usedIds = new TwoKeysSet();
  sameAppCampaigns.forEach((camp) => {
    camp.lineitems?.forEach((li: ILineitem) => {
      if (li.skAdNetworkConfiguration?.defaultCampaignId) {
        usedIds.add(
          li.skAdNetworkConfiguration?.defaultCampaignId,
          li.skAdNetworkConfiguration?.defaultNetworkId ??
            DEFAULT_DEFAULT_NETWORK_ID
        );
      }
    });
  });
  return getAvailableId(usedIds);
}

//calculate defaultCampaignId for various cases: new, updated, unarchavide, copied, created from preset lineitem, etc.
type IArgs = {
  liId?: string;
  liSkadTemplateId?: string;
  parentCampaign: ICampaignServer;
  sameAppCampaigns: ICampaignServer[]; //can include or not parent campaign, doesn't matter
  copiedFromLi?: {
    skAdNetworkConfiguration?: ISkAdNetworkConfiguration;
  } & Record<string, any>; //allow all: Ilineitem, ILineitemFormValues and just ISkAdNetworkConfiguration
};
function calculateDefaultCampaignIdForSkan4Li({
  liId,
  liSkadTemplateId,
  parentCampaign,
  sameAppCampaigns,
  copiedFromLi,
}: IArgs):
  | {
      availableId: IDefaultCampaignIdRange | undefined;
      defaultNetworkId?: DefaultNetwork;
    }
  | undefined
  | null {
  if (!parentCampaign.isSkanSupportedCampaign) return;

  //if has templateId, use defaultCampaignId of any other lineitem with same template
  if (liSkadTemplateId) {
    if (copiedFromLi?.skAdNetworkConfiguration?.defaultCampaignId) {
      return {
        availableId: copiedFromLi?.skAdNetworkConfiguration?.defaultCampaignId,
        defaultNetworkId:
          copiedFromLi?.skAdNetworkConfiguration?.defaultNetworkId,
      };
    }

    let sameTemplateLi: ILineitem | undefined;
    sameAppCampaigns.concat([parentCampaign])?.find((camp) =>
      camp.lineitems?.find((li) => {
        if (li.id === liId || li.id === copiedFromLi?.id) return false; //skip current li (if exists)
        if (
          li.skAdNetworkConfiguration?.templateId === liSkadTemplateId &&
          li.skAdNetworkConfiguration?.defaultCampaignId
        ) {
          sameTemplateLi = li;
          return true;
        }
      })
    );
    if (sameTemplateLi) {
      return {
        availableId: sameTemplateLi.skAdNetworkConfiguration?.defaultCampaignId,
        defaultNetworkId:
          sameTemplateLi.skAdNetworkConfiguration?.defaultNetworkId,
      };
    }
  }

  //otherwise, default assigning strategy
  const nextId = getAvailableIdAcrossSameApp(
    sameAppCampaigns.concat([parentCampaign])
  );
  //if no available id, return null
  return nextId && nextId.availableId ? nextId : null;
}

export { calculateDefaultCampaignIdForSkan4Li };

